/**
 * Self-schedule slot pipeline — orchestrator.
 *
 * availability[] ─▶ subtractBusy ─▶ applyMinimumNotice ─▶ applyTodayBuffer
 *              ─▶ roundUpToGrid ─▶ sliceInterval ─▶ groupByDay
 *
 * Each stage lives in its own tiny file so a failing slot is easy to
 * trace: load one stage's input + output, diff, done. Reuses the
 * existing `availability/` primitives (also used by Manual) so the two
 * modes agree on what "available" means.
 *
 * ## one_on_one with multiple interviewers
 *
 * When `interviewKind === 'one_on_one'` and `scheduleByUserId` carries
 * more than one user, the pipeline subtracts each user's busy from
 * their own working hours and unions the resulting free windows — a
 * slot is offered when **at least one** interviewer is free. Matches
 * legacy `getSelfAvailableSlots`. Without this branch the flat
 * subtraction (`union(availability) − union(busy)`) only emits slots
 * when **every** interviewer is simultaneously free — that's panel
 * semantics applied to one_on_one, which silently zeroed out Find Time
 * for heavily overlapping calendars (sch-30 bug).
 */
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import {
  applyMinimumNotice,
  mergeIntervals,
  subtractBusyFromAvailability,
  type AvailableSlot,
  type BusyEvent,
  type InterviewKind,
  type Interval,
} from '../../../availability';
import { applyTodayBuffer } from './applyTodayBuffer';
import { groupByDay } from './groupByDay';
import { roundUpToGrid } from './roundToGrid';
import { sliceInterval } from './sliceInterval';
import type { DaySlotMap, SelfSlot } from '../types';

export interface RunPipelineArgs {
  availability: Interval[];
  busyEvents: BusyEvent[];
  /** Slot width in minutes. Slots are back-to-back at this width. */
  duration: number;
  /** Target timezone — drives day boundaries and the "now" anchor. */
  timezone: string;
  /** Lead time (hours) before the earliest bookable slot. */
  minimumNoticeHours?: number;
  /**
   * Optional buffer around each generated slot (minutes). 0/0 keeps
   * the original back-to-back behavior. When set, the slicer pushes
   * the first slot in by `before`, strides by `duration + before + after`,
   * and stops with `after` clearance before the gap end — so the
   * interviewer always has prep / wrap-up time on either side.
   */
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  /**
   * Optional now-override for tests/snapshots. Defaults to the current
   * wall time in `timezone`.
   */
  nowMs?: number;
  /**
   * Per-interviewer working hours. Required (alongside `interviewKind`)
   * to enable the one_on_one branch — see the file header. With 0 or
   * 1 entries, or when `interviewKind` !== 'one_on_one', the pipeline
   * falls back to flat subtraction unchanged.
   */
  scheduleByUserId?: Record<string, Interval[]>;
  interviewKind?: InterviewKind;
}

export function runSlotPipeline({
  availability,
  busyEvents,
  duration,
  timezone,
  minimumNoticeHours = 0,
  bufferBeforeMinutes = 0,
  bufferAfterMinutes = 0,
  nowMs = moment().tz(timezone).valueOf(),
  scheduleByUserId,
  interviewKind,
}: RunPipelineArgs): DaySlotMap {
  if (duration <= 0) return {};

  // Stage 1 + 2: availability − busy.
  // - one_on_one with >1 users → per-user subtract, then UNION. A slot
  //   counts as bookable when any interviewer is free.
  // - Anything else → flat subtraction against the pre-aggregated
  //   `availability` (`useCommonAvailability` already returns union for
  //   one_on_one / intersection for panel, so the legacy path is
  //   correct for panel + single-user one_on_one).
  const usePerUser = shouldUsePerUser(interviewKind, scheduleByUserId);

  // Compute considered dates early — every early-return path below
  // must still seed empty entries for in-window days so the cache
  // fallback in `buildActiveSlots` can tell "considered & all-busy"
  // (key present with []) from "outside fetched window" (key absent).
  // Without this, a fully-busy day before noticeAdjusted hits the
  // early-return and is omitted → cache fallback re-emits stale slots
  // on top of busy events (the SCH-46 QA bug).
  const consideredDateKeys = collectConsideredDateKeys({
    availability,
    scheduleByUserId,
    usePerUser,
    timezone,
  });
  const seedConsidered = (grouped: DaySlotMap): DaySlotMap => {
    for (const key of consideredDateKeys) {
      if (!grouped[key]) grouped[key] = [];
    }
    return grouped;
  };

  const rawGaps = computeRawGaps({
    availability,
    busyEvents,
    duration,
    timezone,
    scheduleByUserId,
    interviewKind,
    nowMs,
  });

  if (rawGaps.length === 0) return seedConsidered({});

  // Stage 3: minimum-notice cutoff (pushes gap starts forward).
  const noticeAdjusted = applyMinimumNotice({
    slots: rawGaps,
    noticeHours: minimumNoticeHours,
    timezone,
    minSlotMinutes: duration,
    nowMs,
  });

  // Stage 4 + 5 + 6 per gap: today-buffer → grid-round → slice.
  const flat: SelfSlot[] = [];
  for (const gap of noticeAdjusted) {
    const dateKey = moment.tz(gap.start, timezone).format('YYYY-MM-DD');
    const gapStart = moment.tz(gap.start, timezone).valueOf();
    const gapEnd = moment.tz(gap.end, timezone).valueOf();

    const buffered = applyTodayBuffer(gapStart, nowMs);
    const rounded = roundUpToGrid(buffered);

    flat.push(
      ...sliceInterval({
        startMs: rounded,
        endMs: gapEnd,
        durationMinutes: duration,
        timezone,
        dateKey,
        beforeMinutes: bufferBeforeMinutes,
        afterMinutes: bufferAfterMinutes,
      })
    );
  }

  // Stage 7: bucket by day so state + UI can reason per-column.
  return seedConsidered(groupByDay(flat));
}

/**
 * one_on_one with >1 interviewers takes the per-user union branch; every
 * other case (panel, single-user one_on_one) uses the flat subtraction
 * against the pre-aggregated `availability`. Shared by the generator and
 * the free-window helper so both agree on which branch a mode takes.
 */
export function shouldUsePerUser(
  interviewKind: InterviewKind | undefined,
  scheduleByUserId: Record<string, Interval[]> | undefined
): boolean {
  const userIds = scheduleByUserId ? Object.keys(scheduleByUserId) : [];
  return interviewKind === 'one_on_one' && userIds.length > 1;
}

export interface ComputeRawGapsArgs {
  availability: Interval[];
  busyEvents: BusyEvent[];
  duration: number;
  timezone: string;
  scheduleByUserId?: Record<string, Interval[]>;
  interviewKind?: InterviewKind;
  nowMs?: number;
}

/**
 * Stages 1+2 of the pipeline in isolation: the mode-aware free windows
 * (`availability − busy`) BEFORE minimum-notice / grid-round / slice.
 * Exported so custom-slot re-validation on an interview-kind change can ask
 * the EXACT same question the generator does — "is this window bookable in
 * this mode?" — without re-deriving the union/intersection rules. A custom
 * fully contained in one of these windows is still valid; one that isn't
 * (panel intersection lost it, or it sits over a now-relevant busy event)
 * is dropped.
 */
export function computeRawGaps({
  availability,
  busyEvents,
  duration,
  timezone,
  scheduleByUserId,
  interviewKind,
  nowMs = moment().tz(timezone).valueOf(),
}: ComputeRawGapsArgs): AvailableSlot[] {
  if (duration <= 0) return [];
  if (shouldUsePerUser(interviewKind, scheduleByUserId)) {
    return computePerUserUnionGaps({
      scheduleByUserId: scheduleByUserId!,
      busyEvents,
      timezone,
      minSlotMinutes: duration,
      nowMs,
    });
  }
  if (availability.length === 0) return [];
  return subtractBusyFromAvailability({
    availability,
    busyEvents,
    timezone,
    minSlotMinutes: duration,
    nowMs,
  });
}

interface CollectConsideredArgs {
  availability: Interval[];
  scheduleByUserId?: Record<string, Interval[]>;
  usePerUser: boolean;
  timezone: string;
}

/**
 * The set of `YYYY-MM-DD` keys that the pipeline's INPUT covered. For
 * the per-user branch this is the union of every user's working-hours
 * intervals; for the flat branch it's the common availability spans.
 *
 * Emitting an empty array for these dates (when no slots survive busy
 * subtraction) is what lets `buildActiveSlots` keep the cache fallback
 * scoped to genuinely out-of-window dates only.
 */
function collectConsideredDateKeys({
  availability,
  scheduleByUserId,
  usePerUser,
  timezone,
}: CollectConsideredArgs): Set<string> {
  const out = new Set<string>();
  const addInterval = (iv: { start: string; end: string }) => {
    const start = moment.tz(iv.start, timezone);
    const end = moment.tz(iv.end, timezone);
    if (!start.isValid() || !end.isValid()) return;
    const cursor = start.clone().startOf('day');
    const lastDay = end.clone().startOf('day');
    // Exclusive end at midnight is the same day, not the next one.
    const stopBefore = end.isSame(lastDay)
      ? lastDay
      : lastDay.clone().add(1, 'day');
    while (cursor.isBefore(stopBefore)) {
      out.add(cursor.format('YYYY-MM-DD'));
      cursor.add(1, 'day');
    }
  };
  if (usePerUser && scheduleByUserId) {
    for (const intervals of Object.values(scheduleByUserId)) {
      intervals?.forEach(addInterval);
    }
  } else {
    availability.forEach(addInterval);
  }
  return out;
}

interface PerUserGapsArgs {
  scheduleByUserId: Record<string, Interval[]>;
  busyEvents: BusyEvent[];
  timezone: string;
  minSlotMinutes: number;
  nowMs: number;
}

function computePerUserUnionGaps({
  scheduleByUserId,
  busyEvents,
  timezone,
  minSlotMinutes,
  nowMs,
}: PerUserGapsArgs): AvailableSlot[] {
  // Bucket busy events by interviewer id once so the inner loop is O(N).
  // Keys are stringified — BusyEvent.interviewerId is `string | number`,
  // and scheduleByUserId is always keyed by string.
  const busyByUser = new Map<string, BusyEvent[]>();
  for (const evt of busyEvents) {
    const key = String(evt.interviewerId);
    const bucket = busyByUser.get(key);
    if (bucket) bucket.push(evt);
    else busyByUser.set(key, [evt]);
  }

  const perUserFree: AvailableSlot[] = [];
  for (const [userId, intervals] of Object.entries(scheduleByUserId)) {
    if (!intervals || intervals.length === 0) continue;
    const theirBusy = busyByUser.get(userId) ?? [];
    perUserFree.push(
      ...subtractBusyFromAvailability({
        availability: intervals,
        busyEvents: theirBusy,
        timezone,
        minSlotMinutes,
        nowMs,
      })
    );
  }

  // Union: merge overlapping/touching intervals across users so a
  // downstream slicer doesn't emit the same window once per user.
  // mergeIntervals drops the AvailableSlot tail fields, so re-attach.
  const merged = mergeIntervals(perUserFree, timezone);
  return merged.map((iv) => ({
    id: uuid(),
    start: iv.start,
    end: iv.end,
    dateKey: moment.tz(iv.start, timezone).format('YYYY-MM-DD'),
    // Per-user subtraction already splits by day, so a merged window
    // never spans midnight. Flag stays false for downstream readers.
    crossesMidnight: false,
  }));
}
